import { useCallback, useEffect } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
} from 'lucide-react';
import { Icon } from '@components/base/display';

/**
 * Props for {@link RichTextEditor}. `value`/`onChange` exchange **HTML**
 * strings — the editor renders the incoming HTML and emits the edited HTML, so
 * Fusion templates (which return HTML) display formatted and the composed body
 * is sent as-is to the mail endpoint.
 */
export interface RichTextEditorProps {
  /** Current editor content, as an HTML string. */
  value: string;
  /** Called with the editor's HTML whenever the content changes. */
  onChange: (html: string) => void;
  /** Placeholder shown while the editor is empty. */
  placeholder?: string;
  /** Whether editing is disabled. */
  disabled?: boolean;
  /** Extra classes for the editable region. */
  className?: string;
  /** Accessible label for the editable region. */
  'aria-label'?: string;
}

/** TipTap reports an empty document as `<p></p>` (or whitespace variants). */
function isEmptyHtml(html: string): boolean {
  const stripped = html.replace(/<p>\s*<\/p>/g, '').trim();
  return stripped.length === 0;
}

interface ToolbarButtonProps {
  label: string;
  icon: typeof Bold;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}

function ToolbarButton({ label, icon, active, disabled, onClick }: Readonly<ToolbarButtonProps>) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      // `onMouseDown` preventDefault keeps the editor selection while clicking
      // a toolbar button (otherwise focus leaves the contentEditable region).
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      className={[
        'inline-flex h-8 w-8 items-center justify-center rounded',
        'text-secondary-700 transition-colors',
        'hover:bg-secondary-100',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        active ? 'bg-secondary-200 text-secondary-900' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon icon={icon} size="sm" />
    </button>
  );
}

interface ToolbarProps {
  editor: Editor;
  disabled: boolean;
}

function Toolbar({ editor, disabled }: Readonly<ToolbarProps>) {
  const setLink = useCallback(() => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL du lien', previous ?? 'https://');
    // Cancelled prompt — leave the document untouched.
    if (url === null) return;
    if (url.trim().length === 0) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  }, [editor]);

  return (
    <div
      className="flex flex-wrap items-center gap-1 rounded-t-md border border-b-0 border-secondary-300 bg-secondary-50 px-2 py-1"
      role="toolbar"
      aria-label="Mise en forme"
    >
      <ToolbarButton
        label="Gras"
        icon={Bold}
        active={editor.isActive('bold')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        label="Italique"
        icon={Italic}
        active={editor.isActive('italic')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        label="Souligné"
        icon={UnderlineIcon}
        active={editor.isActive('underline')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      <span className="mx-1 h-5 w-px bg-secondary-300" aria-hidden="true" />
      <ToolbarButton
        label="Liste à puces"
        icon={List}
        active={editor.isActive('bulletList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        label="Liste numérotée"
        icon={ListOrdered}
        active={editor.isActive('orderedList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <span className="mx-1 h-5 w-px bg-secondary-300" aria-hidden="true" />
      <ToolbarButton
        label="Lien"
        icon={LinkIcon}
        active={editor.isActive('link')}
        disabled={disabled}
        onClick={setLink}
      />
    </div>
  );
}

/**
 * Reusable WYSIWYG rich-text editor built on TipTap.
 *
 * Renders a formatting toolbar (bold / italic / underline / bullet & ordered
 * lists / link) above an editable area. Content is exchanged as HTML so it can
 * round-trip Fusion e-mail templates and feed the mail-send endpoint directly.
 *
 * Lives under `claimflow/components` rather than `base/inputs` to avoid the
 * component-explorer showcase/registry requirements that base inputs carry.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled = false,
  className = '',
  'aria-label': ariaLabel,
}: Readonly<RichTextEditorProps>) {
  const editor = useEditor({
    // Avoid SSR/hydration warnings under React 19.
    immediatelyRender: false,
    editable: !disabled,
    extensions: [
      // Disable StarterKit's bundled link/underline so the standalone
      // extensions below register without a duplicate-name conflict.
      StarterKit.configure({ link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false }),
    ],
    content: value,
    onUpdate: ({ editor: current }) => {
      onChange(current.getHTML());
    },
    editorProps: {
      attributes: {
        'aria-label': ariaLabel ?? 'Éditeur de texte enrichi',
        role: 'textbox',
        'aria-multiline': 'true',
        class: [
          'cf-rte-content min-h-[140px] max-h-[400px] overflow-y-auto w-full rounded-b-md border border-secondary-300',
          'px-3 py-2 text-sm text-secondary-800',
          'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500',
          className,
        ]
          .filter(Boolean)
          .join(' '),
      },
    },
  });

  // Keep the editable region in sync with external `value` changes (e.g. a
  // template selection). Only reset when it actually differs, to avoid a
  // cursor-reset loop fed by our own `onUpdate` emissions.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value.trim().length === 0 ? '<p></p>' : value;
    if (next !== current) {
      // `emitUpdate: false` prevents this programmatic sync from re-triggering
      // our own `onChange` (which would feed a cursor-reset loop).
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, value]);

  // Reflect `disabled` changes onto the live editor instance.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  // Derived from the controlled `value` (the source of truth): user edits flow
  // back through `onChange` → `value`, and template selection sets `value`
  // directly, so this re-renders correctly without effect-driven state.
  const showPlaceholder = Boolean(placeholder) && isEmptyHtml(value);

  return (
    <div className="w-full">
      <Toolbar editor={editor} disabled={disabled} />
      <div className="relative">
        {showPlaceholder && (
          <span
            className="pointer-events-none absolute left-3 top-2 text-sm text-secondary-400"
            aria-hidden="true"
          >
            {placeholder}
          </span>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
